import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { API } from "@/lib/api";

const SESSION_KEY = "roastbot-session";
const MSG_KEY = "roastbot-messages";
const GREETING = { role: "assistant", content: "Oh great, another visitor. Say something quick, my girlfriend is waiting 😭" };

const getSession = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = (window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/-/g, "");
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const loadMessages = () => {
  try {
    const m = JSON.parse(localStorage.getItem(MSG_KEY) || "[]");
    return Array.isArray(m) && m.length ? m : [GREETING];
  } catch {
    return [GREETING];
  }
};

const streamReply = async (message, onDelta) => {
  const res = await fetch(`${API}/roastbot/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: getSession(), message }),
  });
  if (res.status === 429) throw new Error("Bro I'm exhausted. Too many roasts, come back later 😭");
  if (!res.ok || !res.body) throw new Error("My brain lagged. Probably texting my girlfriend. Try again 😭");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.forEach((p) => {
      if (!p.startsWith("data: ")) return;
      const d = JSON.parse(p.slice(6));
      if (d.delta) onDelta(d.delta);
      if (d.error) onDelta(d.error);
    });
  }
};

const Bubble = ({ m, i }) => (
  <div data-testid={`roastbot-msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "rounded-br-sm bg-lime text-ink" : "rounded-bl-sm bg-white/10 text-white"}`}>
      {m.content || <span className="inline-flex gap-1"><span className="animate-bounce">.</span><span className="animate-bounce [animation-delay:120ms]">.</span><span className="animate-bounce [animation-delay:240ms]">.</span></span>}
    </div>
  </div>
);

const Panel = ({ onClose }) => {
  const [messages, setMessages] = useState(loadMessages);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(MSG_KEY, JSON.stringify(messages.slice(-50)));
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const updateLast = (fn) => setMessages((prev) => [...prev.slice(0, -1), { ...prev[prev.length - 1], content: fn(prev[prev.length - 1].content) }]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      await streamReply(text.slice(0, 500), (d) => updateLast((c) => c + d));
    } catch (err) {
      updateLast(() => err.message);
    }
    setBusy(false);
  };

  return (
    <motion.div data-testid="roastbot-panel" initial={{ opacity: 0, y: 20, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 380, damping: 28 }}
      className="flex h-[min(520px,70vh)] w-[min(360px,calc(100vw-2.5rem))] origin-bottom-left flex-col overflow-hidden rounded-[28px] border border-violet/50 bg-plum shadow-[0_20px_60px_rgba(0,0,0,0.55)]">
      <div className="flex items-center gap-3 bg-pink px-4 py-3">
        <img src="/img/roast-bot.png" alt="Roast Bot" className="h-12 w-12 -rotate-6" />
        <div className="flex-1">
          <p className="font-display text-base font-extrabold uppercase leading-none">Roast Bot</p>
          <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold"><span className="h-2 w-2 animate-pulse rounded-full bg-lime" /> Busy with girlfriend</p>
        </div>
        <button data-testid="roastbot-close" onClick={onClose} aria-label="Close Roast Bot" className="rounded-full p-1 hover:bg-black/15"><X className="h-5 w-5" /></button>
      </div>
      <div ref={listRef} data-lenis-prevent data-testid="roastbot-messages" className="flex-1 space-y-3 overflow-y-auto p-4">
        {messages.map((m, i) => <Bubble key={i} m={m} i={i} />)}
      </div>
      <form onSubmit={send} className="flex items-center gap-2 border-t border-violet/40 p-3">
        <input data-testid="roastbot-input" value={input} onChange={(e) => setInput(e.target.value)} maxLength={500} placeholder="Say something... if you dare"
          className="flex-1 rounded-full border border-violet/50 bg-ink px-4 py-2.5 text-sm text-white placeholder:text-haze/70 focus:border-lime focus:outline-none" />
        <motion.button data-testid="roastbot-send" whileTap={{ scale: 0.9 }} type="submit" disabled={busy || !input.trim()} aria-label="Send"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-lime text-ink disabled:opacity-50"><Send className="h-4 w-4" /></motion.button>
      </form>
    </motion.div>
  );
};

export const RoastBot = () => {
  const [open, setOpen] = useState(false);
  const [teaser, setTeaser] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setTeaser(true), 4000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="fixed bottom-5 left-5 z-50 flex flex-col items-start gap-3 md:bottom-8 md:left-8">
      <AnimatePresence>{open && <Panel key="panel" onClose={() => setOpen(false)} />}</AnimatePresence>
      <div className="flex items-center gap-3">
        <motion.button data-testid="roastbot-toggle" onClick={() => { setOpen((o) => !o); setTeaser(false); }}
          whileHover={{ scale: 1.1, rotate: -8 }} whileTap={{ scale: 0.9 }}
          aria-label="Chat with Roast Bot" className="h-[72px] w-[72px] drop-shadow-[0_8px_20px_rgba(255,0,122,0.45)]">
          <motion.img src="/img/roast-bot.png" alt="Roast Bot" className="h-full w-full"
            animate={{ y: [0, -5, 0] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} />
        </motion.button>
        <AnimatePresence>
          {teaser && !open && (
            <motion.button data-testid="roastbot-teaser" onClick={() => { setOpen(true); setTeaser(false); }}
              initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}
              className="rounded-2xl rounded-bl-sm bg-white px-3 py-2 font-display text-xs font-extrabold uppercase text-ink shadow-lg">
              <span className="sm:hidden">Get roasted</span><span className="hidden sm:inline">Come get roasted</span>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
